import { useState, useCallback, useEffect, useRef } from 'react';
import { ImageOptimizeWarehouse } from './ImageOptimizeWarehouse';
import { ImageOptimizeStage } from './ImageOptimizeStage';
import { ImageOptimizeControls } from './ImageOptimizeControls';
import { useImageOptimizeState } from './useImageOptimizeState';

const LEFT_PANEL_WIDTH_PERCENT = 0.21;
const MIN_LEFT_PANEL_WIDTH = 280;
const MAX_LEFT_PANEL_WIDTH = 450;

const getInitialLeftWidth = () => {
  const w = window.innerWidth * LEFT_PANEL_WIDTH_PERCENT;
  return Math.min(Math.max(w, MIN_LEFT_PANEL_WIDTH), MAX_LEFT_PANEL_WIDTH);
};

export function ImageOptimize() {
  const { state, setState, setFile, reset } = useImageOptimizeState();
  const [leftWidth, setLeftWidth] = useState(getInitialLeftWidth);
  const [isResizing, setIsResizing] = useState(false);
  const startX = useRef(0);
  const startW = useRef(0);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    startX.current = e.clientX;
    startW.current = leftWidth;
    setIsResizing(true);
  }, [leftWidth]);

  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e: MouseEvent) => {
      const w = startW.current + (e.clientX - startX.current);
      setLeftWidth(Math.min(Math.max(w, MIN_LEFT_PANEL_WIDTH), MAX_LEFT_PANEL_WIDTH));
    };
    const onUp = () => setIsResizing(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isResizing]);

  useEffect(() => {
    if (isResizing) return;
    const onResize = () => setLeftWidth(getInitialLeftWidth());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isResizing]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-1 overflow-hidden">
        <aside className="flex-shrink-0 border-r border-neutral-200 relative"
          style={{ width: `${leftWidth}px` }}>
          <ImageOptimizeWarehouse state={state} setState={setState} setFile={setFile} />
          <div
            className={`absolute top-0 right-0 bottom-0 w-1 cursor-col-resize hover:bg-orange-500 transition-colors ${isResizing ? 'bg-orange-500' : 'bg-transparent'}`}
            onMouseDown={handleResizeStart}
            title="Breite anpassen"
          />
        </aside>
        <section className="flex-1 border-r border-neutral-200">
          <ImageOptimizeStage state={state} setState={setState} reset={reset} />
        </section>
        <aside className="flex-shrink-0 w-[440px] bg-neutral-50 overflow-y-auto">
          <ImageOptimizeControls state={state} setState={setState} />
        </aside>
      </div>
    </div>
  );
}
